import { createCloudRunAxios } from './axios';

const mlsAxios = createCloudRunAxios('mls');

/**
 * Data structure for MLS upload request
 * @param mlsBoardCode - The MLS board code (e.g., "COLB" for Columbus)
 * @param mlsLogo - The URL of the logo image (must be uploaded separately)
 * @param complianceText - The compliance text with autopopulate tags:
 *   - {yyyy} - Current year
 *   - {mm/dd/yyyy hh:mm:ss AM/PM} - Full datetime
 *   - {mm/dd/yyyy} - Date only
 *   - {tenant_name} - Name of the tenant
 * @param agentId - Array of agent IDs to associate with this MLS
 * @param officeId - Array of office IDs to associate with this MLS
 * @param tenantId - The tenant ID to associate with this MLS
 */
interface MLSUploadData {
  mlsBoardCode: string;
  mlsLogo: string;
  complianceText: string;
  agentId?: string[];
  officeId?: string[];
  tenantId?: string;
}

interface MLSUploadResponse {
  status: string;
  message: string;
}

/**
 * Uploads MLS data to the server
 * @param data MLSUploadData object containing:
 *   - mlsBoardCode: The MLS board code (e.g., "COLB")
 *   - mlsLogo: URL of the logo image (must be uploaded separately)
 *   - complianceText: Compliance text with autopopulate tags
 *   - agentId: (optional) Array of agent IDs to associate with this MLS
 *   - officeId: (optional) Array of office IDs to associate with this MLS
 *   - tenantId: (optional) The tenant ID to associate with this MLS
 * @returns Promise resolving to MLSUploadResponse
 */
export const uploadMLSData = async (data: MLSUploadData): Promise<MLSUploadResponse> => {
  const response = await mlsAxios.post('/upload', data);
  return response.data;
};

/**
 * Interface for MLS data response
 */
interface MLSDataResponse {
  status: string;
  data: {
    mlsBoardCode: string;
    mlsLogo: string;
    complianceText: string;
  };
}

/**
 * Fetches MLS data by board code
 * @param mlsBoardCode The MLS board code to fetch data for
 * @returns Promise resolving to MLSDataResponse
 */
export const fetchMLSData = async (mlsBoardCode: string): Promise<MLSDataResponse> => {
  const response = await mlsAxios.post('/access', { mlsBoardCode });
  return response.data;
};

// --- Spanner-backed subscriptions + allowlist (replaces direct Firestore access) ---

/** Approved-MLS allowlist entry — same shape as the legacy MLSLists collection. */
export interface ApprovedMLS {
  boardCode: string;
  mlsName: string;
  isApproved: boolean;
}

/** List all MLS boards (allowlist). Backend returns them approved-first. */
export const fetchApprovedMLSList = async (): Promise<ApprovedMLS[]> => {
  const response = await mlsAxios.post('/approved-mls', {});
  return (response.data.approvedMLS ?? []) as ApprovedMLS[];
};

/** Add or update an allowlist entry. */
export const upsertApprovedMLS = async (input: ApprovedMLS): Promise<void> => {
  await mlsAxios.post('/approved-mls', {
    boardCode: input.boardCode,
    mlsName: input.mlsName,
    isApproved: input.isApproved,
  });
};

/** Remove an allowlist entry. */
export const deleteApprovedMLS = async (boardCode: string): Promise<void> => {
  await mlsAxios.post('/approved-mls', { boardCode, delete: true });
};

/** An org's subscribed MLS boards. */
export interface SubscribedMLS {
  board: string;
  enabled: boolean;
}

export const fetchSubscribedMLS = async (organizationId: string): Promise<SubscribedMLS[]> => {
  const response = await mlsAxios.post('/subscriptions', { org_id: organizationId });
  return (response.data.subscribedMLS ?? []) as SubscribedMLS[];
};

export const unsubscribeMLS = async (organizationId: string, boardCode: string): Promise<void> => {
  await mlsAxios.post('/unsubscribe', { org_id: organizationId, board: boardCode });
};
